/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
'use strict'

import { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSkullCrossbones } from '@fortawesome/free-solid-svg-icons';

interface AddMatchDialogProps {
    show: boolean;
    setShow: (show: boolean) => void;
    editFp: any;
    setEditFp: (next: any) => void;
    editMatchIndex?: number | null;
    setEditMatchIndex?: (index: number | null) => void;
}

const EMPTY_MATCH = {
    ip: '',
    reverse_dns: '',
    forward_dns: '',
    recent_dns: '',
    tls_cert_subj: '',
    tls_sni: '',
    tls_alpn: '',
};

function isDnsHostname(host: string): boolean {
    if (!host || !host.includes('.') || host.length > 253) {
        return false;
    }
    if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) {
        return false;
    }
    const labels = host.split('.');
    if (labels.length < 2) {
        return false;
    }
    for (const label of labels) {
        if (!label || label.length > 63) {
            return false;
        }
        if (!/^[a-z0-9-]+$/.test(label)) {
            return false;
        }
        if (label.startsWith('-') || label.endsWith('-')) {
            return false;
        }
    }
    const tld = labels[labels.length - 1];
    if (/^\d+$/.test(tld)) {
        return false;
    }
    return true;
}

function isValidBareFqdn(value: string): boolean {
    const raw = String(value || '').trim();
    if (!raw) {
        return false;
    }
    if (/\s/.test(raw) || /[/?#@]/.test(raw)) {
        return false;
    }
    if (raw.includes('://') || raw.includes('\\')) {
        return false;
    }
    if (raw.includes(':')) {
        return false;
    }
    if (/^["'`]|["'`]$/.test(raw)) {
        return false;
    }
    if (raw.startsWith('*.') || raw.endsWith('.')) {
        return false;
    }
    return isDnsHostname(raw.toLowerCase());
}

function fqdnInvalid(value: any): boolean {
    const txt = String(value || '').trim();
    if (!txt) {
        return false;
    }
    return !isValidBareFqdn(txt);
}

function AddMatchDialog({ show, setShow, editFp, setEditFp, editMatchIndex = null, setEditMatchIndex }: AddMatchDialogProps) {
    const [matchEntry, setMatchEntry] = useState<any>(EMPTY_MATCH);

    useEffect(() => {
        if (!show) {
            return;
        }
        if (Number.isInteger(editMatchIndex) && editMatchIndex >= 0 && Array.isArray(editFp?.matches)) {
            const source = editFp.matches[editMatchIndex] || {};
            setMatchEntry({
                ...EMPTY_MATCH,
                ...source,
            });
            return;
        }
        setMatchEntry(EMPTY_MATCH);
    }, [show, editMatchIndex, editFp?.matches]);

    const editing = Number.isInteger(editMatchIndex) && editMatchIndex >= 0;
    const invalidForwardDns = fqdnInvalid(matchEntry.forward_dns);
    const invalidRecentDns = fqdnInvalid(matchEntry.recent_dns);
    const invalidTlsSni = fqdnInvalid(matchEntry.tls_sni);

    const handleClose = () => {
        setShow(false);
        if (setEditMatchIndex) {
            setEditMatchIndex(null);
        }
    };

  return (
  <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Edit Match' : 'Add Match'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
            <Form>
                <Row>
                    <Form.Group as={Col} className="mb-3" controlId="f_id">
                        <Form.Label>IP</Form.Label>
                        <Form.Control 
                            type="text" 
                            value={matchEntry.ip} 
                            onChange={e => {
                                let newValue = e.target.value;
                                setMatchEntry({
                                    ...matchEntry,
                                    ip: newValue
                                });
                            }}
                        />
                        <Form.Text className="text-muted">
                            IPV4 or IPV6 literal address
                        </Form.Text>
                    </Form.Group>
                    <Form.Group as={Col} className="mb-3" controlId="f_id">
                        <Form.Label>Reverse DNS</Form.Label>
                        <Form.Control 
                            type="text" 
                            value={matchEntry.reverse_dns} 
                            onChange={e => {
                                let newValue = e.target.value;
                                setMatchEntry({
                                    ...matchEntry,
                                    reverse_dns: newValue
                                });
                            }}
                        />
                        <Form.Text className="text-muted">
                            Reverse DNS name for an IP address.
                        </Form.Text>
                    </Form.Group>
                    <Form.Group as={Col} className="mb-3" controlId="f_forward_dns">
                        <Form.Label>
                            Forward DNS
                            {invalidForwardDns ? (
                                <span className="ms-2 text-warning" title="Invalid bare fqdn">
                                    <FontAwesomeIcon icon={faSkullCrossbones} size="sm" />
                                </span>
                            ) : null}
                        </Form.Label>
                        <Form.Control 
                            type="text" 
                            value={matchEntry.forward_dns} 
                            isInvalid={invalidForwardDns}
                            onChange={e => {
                                let newValue = e.target.value;
                                setMatchEntry({
                                    ...matchEntry,
                                    forward_dns: newValue
                                });
                            }}
                        />
                        <Form.Text className="text-muted">
                            Forward DNS name for an IP address.
                        </Form.Text>
                    </Form.Group>
                </Row>
                <Row>
                    <Form.Group as={Col} className="mb-3" controlId="f_recent_dns">
                        <Form.Label>
                            Recent DNS
                            {invalidRecentDns ? (
                                <span className="ms-2 text-warning" title="Invalid bare fqdn">
                                    <FontAwesomeIcon icon={faSkullCrossbones} size="sm" />
                                </span>
                            ) : null}
                        </Form.Label>
                        <Form.Control 
                            type="text" 
                            value={matchEntry.recent_dns} 
                            isInvalid={invalidRecentDns}
                            onChange={e => {
                                let newValue = e.target.value;
                                setMatchEntry({
                                    ...matchEntry,
                                    recent_dns: newValue
                                });
                            }}
                        />
                        <Form.Text className="text-muted">
                            Recently resolved DNS name for an IP address.
                        </Form.Text>
                    </Form.Group>
                    <Form.Group as={Col} className="mb-3" controlId="f_tls_certSubject">
                        <Form.Label>TLS Certificate Subject</Form.Label>
                        <Form.Control 
                            type="text" 
                            value={matchEntry.tls_cert_subj} 
                            onChange={e => {
                                let newValue = e.target.value;
                                setMatchEntry({
                                    ...matchEntry,
                                    tls_cert_subj: newValue
                                });
                            }}
                        />
                        <Form.Text className="text-muted">
                            Subject field of the TLS certificate.
                        </Form.Text>
                    </Form.Group>
                </Row>
                <Row>
                    <Form.Group as={Col} className="mb-3" controlId="f_tls_sni">
                        <Form.Label>
                            TLS SNI
                            {invalidTlsSni ? (
                                <span className="ms-2 text-warning" title="Invalid bare fqdn">
                                    <FontAwesomeIcon icon={faSkullCrossbones} size="sm" />
                                </span>
                            ) : null}
                        </Form.Label>
                        <Form.Control 
                            type="text" 
                            value={matchEntry.tls_sni} 
                            isInvalid={invalidTlsSni}
                            onChange={e => {
                                let newValue = e.target.value;
                                setMatchEntry({
                                    ...matchEntry,
                                    tls_sni: newValue
                                });
                            }}
                        />
                        <Form.Text className="text-muted">
                            Server Name Indication (SNI) used in the TLS handshake.
                        </Form.Text>
                    </Form.Group>
                    <Form.Group as={Col} className="mb-3" controlId="f_tls_alpn">
                        <Form.Label>TLS ALPN</Form.Label>
                        <Form.Control 
                            type="text" 
                            value={matchEntry.tls_alpn} 
                            onChange={e => {
                                let newValue = e.target.value;
                                setMatchEntry({
                                    ...matchEntry,
                                    tls_alpn: newValue
                                });
                            }}
                        />
                        <Form.Text className="text-muted">
                            Application-Layer Protocol Negotiation (ALPN) used in the TLS handshake.
                        </Form.Text>
                    </Form.Group>
                </Row>
            </Form>
      </Modal.Body>
      <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
              Close
          </Button>
          <Button variant="primary" onClick={() => {
                const nextMatch = {
                    ...EMPTY_MATCH,
                    ...matchEntry,
                };
                const matches = [...(editFp.matches || [])];
                if (editing) {
                    matches[editMatchIndex] = {
                        ...(matches[editMatchIndex] || {}),
                        ...nextMatch,
                    };
                } else {
                    matches.push(nextMatch);
                }
                setEditFp({ ...editFp, matches } as any);
                handleClose();
          }}>
              {editing ? 'Save Match' : 'Add Match'}
          </Button>
      </Modal.Footer>
  </Modal>
  );
}

export default AddMatchDialog;
